import { Handle, Position, useUpdateNodeInternals } from "@xyflow/react";
import { Form, Formik, FormikProps } from "formik";
import { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { IMeasurementTypeOptions } from "../../../../../../@types/Measurements";
import {
  IWorkflowsSelectOption,
  IWorkflowRule,
  IWorkflowsSurveyOption,
  IWorkflowTimezoneOption,
} from "../../../../../../@types/Worflows";
import { SelectDropdown } from "../../../../../../components/SelectDropdown";
import { getWorkflowOptionLabel } from "../../../../../../utils/workflowOptionLabels";
import { ErrorMessage, selectCss } from "../../WorkflowsBuilder.styles";
import {
  MeasurementFields,
  SurveyResponseFields,
  SurveyScoreFields,
  InactivityFields,
  FibricheckStatusFields,
  AliveCorEcgStatusFields,
  SpirometryStatusFields,
  SleepFields,
  ActivityFields,
  DateEventFields,
} from "./fields";
import {
  Container,
  Title,
  BottomContainer,
  TrueOrFalseBox,
  SelectContainer,
} from "./RuleComponent.styles";
import {
  emptyRuleConditionFormValues,
  toRuleConditionFormValues,
} from "./ruleConditionFormValues";
import { isScheduledRuleType } from "./scheduledRuleTypes";
import { createRuleValidationSchema } from "./validation/ruleValidationSchemas";

interface IRuleComponentBaseProps {
  ruleTypes?: IWorkflowsSelectOption[];
  ruleConditionComparators?: IWorkflowsSelectOption[];
  surveyScoreConditionComparators?: IWorkflowsSelectOption[];
  ruleConditionTriggers?: IWorkflowsSelectOption[];
  measurementTypes?: IMeasurementTypeOptions[];
  surveyOptions?: IWorkflowsSurveyOption[];
  timezoneOptions?: IWorkflowTimezoneOption[];
  hospitalTimezone?: string;
  fibricheckStatuses?: IWorkflowsSelectOption[];
  aliveCorEcgStatuses?: IWorkflowsSelectOption[];
  sleepStages?: IWorkflowsSelectOption[];
  activityMetrics?: IWorkflowsSelectOption[];
  spirometryStatuses?: IWorkflowsSelectOption[];
  occurrencePeriods?: IWorkflowsSelectOption[];
  dateEventTypes?: IWorkflowsSelectOption[];
  isFirstStep?: boolean;
  /** Whether another rule in the graph is already a scheduled (Inactivity / Date) rule. */
  hasScheduledRule?: boolean;
  isExistingRule?: boolean;
}

interface IRuleComponentFormikProps extends IRuleComponentBaseProps {
  content?: IWorkflowRule;
}

interface IRuleComponentProps extends IRuleComponentBaseProps {
  formik: FormikProps<IWorkflowRule>;
}

function RuleComponent({
  ruleTypes,
  ruleConditionComparators,
  surveyScoreConditionComparators,
  ruleConditionTriggers,
  measurementTypes,
  surveyOptions,
  timezoneOptions,
  hospitalTimezone,
  fibricheckStatuses,
  aliveCorEcgStatuses,
  sleepStages,
  activityMetrics,
  spirometryStatuses,
  occurrencePeriods,
  dateEventTypes,
  isFirstStep,
  hasScheduledRule,
  isExistingRule,
  formik,
}: IRuleComponentProps) {
  const { t } = useTranslation();

  const shouldShowRuleTypeError = (): string => {
    return formik.touched.ruleType && formik.errors.ruleType
      ? (formik.errors.ruleType as string)
      : "";
  };

  const handleResetForm = () => {
    formik.resetForm({
      values: {
        ruleType: undefined,
        measurementType: undefined,
        conditions: [emptyRuleConditionFormValues()],
      },
    });
  };

  // Filter rule types based on position
  const getAvailableRuleTypes = () => {
    if (!ruleTypes) return [];

    return ruleTypes
      .filter((ruleType) => {
        // If editing an existing scheduled rule (Inactivity / Date), only show that same option
        // (prevents changing from a scheduled type to other types when editing)
        if (isExistingRule && isScheduledRuleType(formik.values.ruleType)) {
          return ruleType.type === formik.values.ruleType;
        }

        // Scheduled rules can only be the first step, and a workflow holds at most one of them
        if (isScheduledRuleType(ruleType.type)) {
          return isFirstStep && !hasScheduledRule;
        }
        return true;
      })
      .map((ruleType) => ({
        label: getWorkflowOptionLabel("ruleType", ruleType, t),
        value: ruleType.type,
        key: ruleType.type,
      }));
  };

  return (
    <Container>
      <Title>{t("workflows_rule_title")}</Title>

      <SelectContainer isError={shouldShowRuleTypeError() !== ""}>
        <p>{t("workflows_rule_type")}</p>
        <SelectDropdown
          placeholder={t("workflows_rule_typePlaceholder")}
          value={formik.values.ruleType}
          options={getAvailableRuleTypes()}
          onValueChange={(value) => {
            handleResetForm();
            formik.setFieldValue("ruleType", value as string);
          }}
          className={`${selectCss()} nopan nodrag`}
        />
        <ErrorMessage>{shouldShowRuleTypeError()}</ErrorMessage>
      </SelectContainer>

      {/* Measurement Fields */}
      {formik.values.ruleType === "Measurement" && (
        <MeasurementFields
          formik={formik}
          ruleConditionComparators={ruleConditionComparators}
          ruleConditionTriggers={ruleConditionTriggers}
          measurementTypes={measurementTypes}
        />
      )}

      {/* Survey Response Fields */}
      {formik.values.ruleType === "SurveyResponse" && (
        <SurveyResponseFields formik={formik} surveyOptions={surveyOptions} />
      )}

      {/* Survey Score Fields */}
      {formik.values.ruleType === "SurveyScore" && (
        <SurveyScoreFields
          formik={formik}
          surveyScoreConditionComparators={surveyScoreConditionComparators}
          surveyOptions={surveyOptions}
        />
      )}

      {/* Inactivity Fields */}
      {formik.values.ruleType === "Inactivity" && (
        <InactivityFields
          formik={formik}
          timezoneOptions={timezoneOptions}
          hospitalTimezone={hospitalTimezone}
        />
      )}

      {/* Fibricheck Status Fields */}
      {formik.values.ruleType === "FibricheckStatus" && (
        <FibricheckStatusFields
          formik={formik}
          fibricheckStatuses={fibricheckStatuses}
        />
      )}

      {/* AliveCor ECG Status Fields */}
      {formik.values.ruleType === "AliveCorEcgStatus" && (
        <AliveCorEcgStatusFields
          formik={formik}
          aliveCorEcgStatuses={aliveCorEcgStatuses}
        />
      )}

      {/* Sleep Fields */}
      {formik.values.ruleType === "Sleep" && (
        <SleepFields
          formik={formik}
          ruleConditionComparators={ruleConditionComparators}
          ruleConditionTriggers={ruleConditionTriggers}
          sleepStages={sleepStages}
        />
      )}

      {/* Activity Fields */}
      {formik.values.ruleType === "Activity" && (
        <ActivityFields
          formik={formik}
          ruleConditionComparators={ruleConditionComparators}
          activityMetrics={activityMetrics}
        />
      )}

      {/* Spirometry Status Fields */}
      {formik.values.ruleType === "SpirometryStatus" && (
        <SpirometryStatusFields
          formik={formik}
          spirometryStatuses={spirometryStatuses}
          occurrencePeriods={occurrencePeriods}
        />
      )}

      {/* Date Event Fields */}
      {formik.values.ruleType === "DateEvent" && (
        <DateEventFields
          formik={formik}
          dateEventTypes={dateEventTypes}
          hospitalTimezone={hospitalTimezone}
        />
      )}

      {!isScheduledRuleType(formik.values.ruleType) && (
        <BottomContainer>
          <TrueOrFalseBox>{t("workflows_rule_true")}</TrueOrFalseBox>
          <TrueOrFalseBox isRed>{t("workflows_rule_false")}</TrueOrFalseBox>
        </BottomContainer>
      )}
    </Container>
  );
}

const RuleComponentFormik = forwardRef<
  FormikProps<IWorkflowRule>,
  IRuleComponentFormikProps
>(
  (
    {
      ruleTypes,
      ruleConditionComparators,
      surveyScoreConditionComparators,
      ruleConditionTriggers,
      measurementTypes,
      surveyOptions,
      timezoneOptions,
      hospitalTimezone,
      fibricheckStatuses,
      aliveCorEcgStatuses,
      sleepStages,
      activityMetrics,
      spirometryStatuses,
      occurrencePeriods,
      dateEventTypes,
      isFirstStep,
      hasScheduledRule,
      isExistingRule,
      content,
    },
    ref
  ) => {
    const handleOnSubmitForm = () => {};

    const ruleValidationSchema = createRuleValidationSchema();

    return (
      <Formik<IWorkflowRule>
        innerRef={ref}
        initialValues={{
          ruleType: content?.ruleType,
          measurementType: content?.conditions?.[0]?.measurementType,
          conditions: [toRuleConditionFormValues(content?.conditions?.[0])],
        }}
        onSubmit={handleOnSubmitForm}
        validationSchema={ruleValidationSchema}
        enableReinitialize
      >
        {(formik) => (
          <Form>
            <RuleComponent
              formik={formik}
              ruleTypes={ruleTypes}
              ruleConditionComparators={ruleConditionComparators}
              surveyScoreConditionComparators={surveyScoreConditionComparators}
              ruleConditionTriggers={ruleConditionTriggers}
              measurementTypes={measurementTypes}
              surveyOptions={surveyOptions}
              timezoneOptions={timezoneOptions}
              hospitalTimezone={hospitalTimezone}
              fibricheckStatuses={fibricheckStatuses}
              aliveCorEcgStatuses={aliveCorEcgStatuses}
              sleepStages={sleepStages}
              activityMetrics={activityMetrics}
              spirometryStatuses={spirometryStatuses}
              occurrencePeriods={occurrencePeriods}
              dateEventTypes={dateEventTypes}
              isFirstStep={isFirstStep}
              hasScheduledRule={hasScheduledRule}
              isExistingRule={isExistingRule}
            />
          </Form>
        )}
      </Formik>
    );
  }
);

export function RuleNode(props: any) {
  const { data, id } = props;
  const updateNodeInternals = useUpdateNodeInternals();
  // Scheduled rules (Inactivity / Date) have a single True output
  const [isScheduled, setIsScheduled] = useState(
    isScheduledRuleType(data.content?.ruleType)
  );

  // Watch for ruleType changes in the formik ref
  useEffect(() => {
    const checkRuleType = () => {
      const currentRuleType = data.ref?.current?.values?.ruleType;
      const newIsScheduled = isScheduledRuleType(currentRuleType);
      if (newIsScheduled !== isScheduled) {
        setIsScheduled(newIsScheduled);
      }
    };

    // Check immediately and set up an interval to watch for changes
    checkRuleType();
    const interval = setInterval(checkRuleType, 100);
    return () => clearInterval(interval);
  }, [data.ref, isScheduled]);

  // Update node internals when isScheduled changes
  useEffect(() => {
    // Small delay to ensure DOM has updated
    const timeout = setTimeout(() => {
      updateNodeInternals(id);
    }, 50);
    return () => clearTimeout(timeout);
  }, [isScheduled, id, updateNodeInternals]);

  return (
    <>
      {!data.isFirstStep ? (
        <Handle id="target" type="target" position={Position.Top} />
      ) : null}
      <RuleComponentFormik
        ruleTypes={data.ruleTypes}
        ruleConditionComparators={data.ruleConditionComparators}
        surveyScoreConditionComparators={data.surveyScoreConditionComparators}
        ruleConditionTriggers={data.ruleConditionTriggers}
        measurementTypes={data.measurementTypeOptions}
        surveyOptions={data.surveyOptions}
        timezoneOptions={data.timezoneOptions}
        hospitalTimezone={data.hospitalTimezone}
        fibricheckStatuses={data.fibricheckStatuses}
        aliveCorEcgStatuses={data.aliveCorEcgStatuses}
        sleepStages={data.sleepStages}
        activityMetrics={data.activityMetrics}
        spirometryStatuses={data.spirometryStatuses}
        occurrencePeriods={data.occurrencePeriods}
        dateEventTypes={data.dateEventTypes}
        isFirstStep={data.isFirstStep}
        hasScheduledRule={data.hasScheduledRule}
        isExistingRule={!!data.content?.ruleType}
        content={data.content}
        ref={data.ref}
      />
      <Handle
        type="source"
        id="true"
        position={Position.Bottom}
        style={{ left: isScheduled ? "50%" : "86px" }}
      />
      {!isScheduled && (
        <Handle
          type="source"
          id="false"
          position={Position.Bottom}
          style={{ left: "240px" }}
        />
      )}
    </>
  );
}
